/** ============================================================================
 * Router
 *
 * @version   1.0.0
 * @author    Sam Rankin <srankin@riester.com>
 * @copyright 2022 RIESTER
 * ========================================================================== */

/**
 * DOM-based Routing
 *
 * Based on {@link http://goo.gl/EUTi53|Markup-based Unobtrusive Comprehensive DOM-ready Execution} by Paul Irish
 *
 * The routing fires all common scripts, followed by the page specific scripts.
 * Add additional events for more control over timing e.g. a finalize event
 */
class Router {
	readyList = [];
	readyFired = false;
	readyEventHandlersInstalled = false;
	routes = {};
	/**
	 * Create a new Router
	 * @param {Object} routes
	 */
	constructor(routes) {
		this.routes = routes;
		this.router = this;
	}

	throttle(cb, delay = 1000) {
		let shouldWait = false;
		let waitingArgs;
		const timeoutFunc = () => {
			if (waitingArgs == null) {
				shouldWait = false;
			} else {
				cb(...waitingArgs);
				waitingArgs = null;
				setTimeout(timeoutFunc, delay);
			}
		};

		return (...args) => {
			if (shouldWait) {
				waitingArgs = args;
				return;
			}

			cb(...args);
			shouldWait = true;
			setTimeout(timeoutFunc, delay);
		};
	}
	promisedEvent(route = {}, eventType = '', timeout = 0, ...args) {
		const event = route[eventType];
		const listener = this.throttle(() => {
			event.apply(route, args);
		}, timeout);

		return new Promise((resolve) => {
			if ('resize' === eventType) {
				window.addEventListener('resize', listener);
			} else if ('init' == eventType) {
				domready(listener);
			} else if ('finalize' == eventType) {
				window.addEventListener('load', listener, false);
			}
			resolve();
		});
	}

	async asyncEvent(route, eventType, timeout = 0, ...args) {
		await this.promisedEvent(route, eventType, timeout, ...args);
	}

	/**
	 * Fire Router events
	 * @param {string} route DOM-based route derived from body classes (`<body class="...">`)
	 * @param {string} [event] Events on the route. By default, `init` and `finalize` events are called.
	 * @param {string} [arg] Any custom argument to be passed to the event.
	 */
	fire(routeName, eventType = 'init', timeout = 0, ...args) {
		document.dispatchEvent(
			new CustomEvent('routed', {
				bubbles: true,
				detail: {
					routeName,
					fn: eventType,
				},
			})
		);

		const route = this.routes[routeName],
			event = route[eventType];

		const fire = routeName !== '' && route && typeof event === 'function';

		try {
			if (fire) {
				this.asyncEvent(route, eventType, timeout, ...args);
			} else if (typeof event !== 'function') {
				throw new TypeError(`callback for ${route.name}.${eventType} must be a function`);
			} else {
				throw new Error(`cannot run ${route.name}.${eventType}`);
			}
		} catch (e) {
			// statements to handle any exceptions
			console.error('Router Error:', e.message);
		}
	}

	/**
	 * Automatically load and fire Router events
	 *
	 */
	loadEvents() {
		Object.keys(this.routes).forEach((route) => {
			this.fire(route);
		});
		Object.keys(this.routes).forEach((route) => {
			this.fire(route, 'finalize', 2000);
		});
		Object.keys(this.routes).forEach((route) => {
			this.fire(route, 'resize', 100);
		});
	}
}

export default Router;
